import Konva from 'konva';
import { Router } from '../router/Router';
import { SyncClient } from '../sync/SyncClient';
import { AudioRecorder, SegmentPlayer, AudioSegment } from '../audio/AudioRecorder';

// Base canvas dimensions (will be scaled)
const BASE_WIDTH = 400;
const BASE_HEIGHT = 500;

// Official ITF tennis court dimensions (feet)
const COURT = {
  length: 78,
  singlesWidth: 27,
  doublesWidth: 36,
  serviceLineFromNet: 21,
  halfCourt: 39,
  netHeightCenter: 3,
  netHeightPost: 3.5,
  serviceBoxWidth: 13.5,
  doublesAlley: 4.5
};

const PERSPECTIVE_CONFIG = {
  viewerDistance: 40,
  courtMarginTop: 0.36,
  courtMarginBottom: 0.36,
  nearWidthRatio: 0.80,
};

interface Perspective {
  nearLeft: number;
  nearRight: number;
  nearY: number;
  farLeft: number;
  farRight: number;
  farY: number;
  netY: number;
  scaleRatio: number;
}

// Coordinates are stored as ratios (0-1) relative to BASE_WIDTH/BASE_HEIGHT
// This ensures diagrams display correctly across different screen sizes
interface ArrowData {
  startX: number;  // ratio 0-1
  startY: number;  // ratio 0-1
  endX: number;    // ratio 0-1
  endY: number;    // ratio 0-1
  color: string;
  style: string;
  ctrl?: { x: number; y: number } | null;  // ratios 0-1
  bounce?: { x: number; y: number };        // ratios 0-1
}

interface PlayerData {
  x: number;  // ratio 0-1
  y: number;  // ratio 0-1
  color: string;
  label: string;
}

interface State {
  players: PlayerData[];
  arrows: ArrowData[];
}

// Helper functions to convert between pixels and ratios
function pixelToRatio(px: number, base: number): number {
  return px / base;
}

function ratioToPixel(ratio: number, current: number): number {
  return ratio * current;
}

interface Slide {
  id: string;
  description: string;
  canvasState: State;
}

interface DiagramState {
  description: string;
  slides: Slide[];
  currentSlideIndex: number;
  audioSegments: AudioSegment[];
}

export class DiagramsApp {
  private element: HTMLElement;
  private router: Router;
  private diagramId?: string;

  // Canvas state
  private CANVAS_WIDTH = BASE_WIDTH;
  private CANVAS_HEIGHT = BASE_HEIGHT;
  private PERSPECTIVE: Perspective;

  // Konva objects
  private stage!: Konva.Stage;
  private backgroundLayer!: Konva.Layer;
  private courtLayer!: Konva.Layer;
  private arrowLayer!: Konva.Layer;
  private playerLayer!: Konva.Layer;
  private uiLayer!: Konva.Layer;

  // App state
  private currentMode = 'select';
  private currentArrowStyle = 'curved';
  private selectedShape: Konva.Group | null = null;
  private isDrawing = false;
  private drawStartPos: { x: number; y: number } | null = null;
  private tempArrow: Konva.Group | null = null;
  private hitCounter = 0;

  // Slide state
  private slides: Slide[] = [];
  private currentSlideIndex = 0;
  private STORAGE_KEY = 'tennis-diagram-state';

  // Undo/Redo
  private undoStack: string[] = [];
  private redoStack: string[] = [];
  private MAX_UNDO = 50;

  // DOM references
  private canvasWrapper!: HTMLElement;
  private undoBtn!: HTMLButtonElement;
  private redoBtn!: HTMLButtonElement;
  private titleInput!: HTMLInputElement;
  private descriptionInput!: HTMLDivElement;
  private helpModal!: HTMLElement;
  private deleteSlideBtn!: HTMLButtonElement;
  private prevSlideBtn!: HTMLButtonElement;
  private nextSlideBtn!: HTMLButtonElement;
  private slideIndicator!: HTMLElement;
  private addSlideBtn!: HTMLButtonElement;

  // Audio segments (diagram-level)
  private audioSegments: AudioSegment[] = [];
  private audioRecorder: AudioRecorder | null = null;
  private segmentPlayer: SegmentPlayer | null = null;
  private mediaBar!: HTMLElement;
  private recordBtn!: HTMLButtonElement;
  private playAllBtn!: HTMLButtonElement;
  private segmentTimeline!: HTMLElement;
  private audioTimer!: HTMLElement;
  private isRecording = false;
  private isPlaying = false;

  // Event handlers (for cleanup)
  private boundHandleResize: () => void;
  private boundHandleKeydown: (e: KeyboardEvent) => void;
  private resizeTimeout?: number;

  // Sync client for real-time collaboration
  private syncClient: SyncClient | null = null;
  private syncDebounceTimer?: number;

  constructor(container: HTMLElement, router: Router, diagramId?: string) {
    this.router = router;
    this.diagramId = diagramId;
    this.PERSPECTIVE = this.getPerspective();

    this.element = document.createElement('div');
    this.element.className = 'diagram-editor';
    this.render();
    container.appendChild(this.element);

    this.boundHandleResize = this.handleResize.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);

    this.init();
  }

  private render(): void {
    this.element.innerHTML = `
      <div class="canvas-area">
        <div class="description-overlay">
          <div id="description-input" contenteditable="true" data-placeholder="Tap to add description..." spellcheck="false"></div>
        </div>
        <div id="canvas-wrapper"></div>
      </div>

      <input type="text" id="title-input" value="Untitled Diagram" spellcheck="false" hidden />

      <div class="toolbar">
        <div class="tool-section">
          <button id="help-btn" class="tool-btn" title="Help">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </button>
        </div>

        <div class="tool-section">
          <button id="undo-btn" class="tool-btn" title="Undo (Ctrl+Z)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 10h10a5 5 0 0 1 5 5v2"/>
              <polyline points="3 10 8 5"/>
              <polyline points="3 10 8 15"/>
            </svg>
          </button>
          <button id="redo-btn" class="tool-btn" title="Redo (Ctrl+Shift+Z)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 10H11a5 5 0 0 0-5 5v2"/>
              <polyline points="21 10 16 5"/>
              <polyline points="21 10 16 15"/>
            </svg>
          </button>
        </div>

        <div class="tool-section pagination">
          <button id="prev-slide" class="tool-btn" title="Previous Slide">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <span id="slide-indicator" class="slide-indicator">1/1</span>
          <button id="next-slide" class="tool-btn" title="Next Slide">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
          <button id="add-slide" class="tool-btn" title="Add Slide">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </button>
          <button id="delete-slide" class="tool-btn" title="Delete Slide">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </button>
        </div>

      </div>

      <!-- Record Button - Centered above media bar -->
      <button id="record-btn" class="record-btn-floating" title="Record Audio">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
      </button>

      <!-- Media Bar for audio segments -->
      <div class="media-bar" id="media-bar">
        <button id="play-all-btn" class="tool-btn" title="Play All">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="5 3 19 12 5 21 5 3"/>
          </svg>
        </button>
        <div class="segment-timeline" id="segment-timeline">
          <span class="empty-message">No recordings yet</span>
        </div>
        <span id="audio-timer" class="audio-timer"></span>
      </div>

      <div class="help-modal" id="help-modal">
        <div class="help-content">
          <h2>How to Use</h2>
          <ul>
            <li><strong>Draw shots</strong>Drag on court to draw shot trajectories</li>
            <li><strong>Adjust</strong>Tap arrow to select, drag handles to adjust</li>
            <li><strong>Colors</strong>Top→down = red (opponent), bottom→up = green (you)</li>
            <li><strong>Delete</strong>Select and tap trash icon or press Delete</li>
            <li><strong>Players</strong>Drag player markers to position</li>
          </ul>
          <button class="help-close" id="help-close">Got it</button>
        </div>
      </div>
    `;
  }

  private init(): void {
    // Get DOM references
    this.canvasWrapper = this.element.querySelector('#canvas-wrapper')!;
    this.undoBtn = this.element.querySelector('#undo-btn')!;
    this.redoBtn = this.element.querySelector('#redo-btn')!;
    this.titleInput = this.element.querySelector('#title-input')!;
    this.descriptionInput = this.element.querySelector('#description-input')!;
    this.helpModal = this.element.querySelector('#help-modal')!;
    this.prevSlideBtn = this.element.querySelector('#prev-slide')!;
    this.nextSlideBtn = this.element.querySelector('#next-slide')!;
    this.slideIndicator = this.element.querySelector('#slide-indicator')!;
    this.addSlideBtn = this.element.querySelector('#add-slide')!;
    this.deleteSlideBtn = this.element.querySelector('#delete-slide')!;
    this.mediaBar = this.element.querySelector('#media-bar')!;
    this.recordBtn = this.element.querySelector('#record-btn')!;
    this.playAllBtn = this.element.querySelector('#play-all-btn')!;
    this.segmentTimeline = this.element.querySelector('#segment-timeline')!;
    this.audioTimer = this.element.querySelector('#audio-timer')!;

    // Get responsive size
    const size = this.getCanvasSize();
    this.CANVAS_WIDTH = size.width;
    this.CANVAS_HEIGHT = size.height;
    this.PERSPECTIVE = this.getPerspective();

    // Create Konva stage
    this.stage = new Konva.Stage({
      container: this.canvasWrapper,
      width: this.CANVAS_WIDTH,
      height: this.CANVAS_HEIGHT
    });

    // Create layers
    this.backgroundLayer = new Konva.Layer();
    this.courtLayer = new Konva.Layer();
    this.arrowLayer = new Konva.Layer();
    this.playerLayer = new Konva.Layer();
    this.uiLayer = new Konva.Layer();

    this.stage.add(this.backgroundLayer);
    this.stage.add(this.courtLayer);
    this.stage.add(this.arrowLayer);
    this.stage.add(this.playerLayer);
    this.stage.add(this.uiLayer);

    // Draw initial state
    this.drawBackground();
    this.drawCourt();
    this.createDefaultPlayers();

    // Bind events
    this.bindEvents();

    // Initialize slides (loads from localStorage or creates first slide)
    this.initializeSlides();

    // Save initial state
    this.saveState();
    this.updateUndoRedoButtons();
  }

  private getCanvasSize(): { width: number; height: number } {
    const container = this.element.querySelector('.canvas-area');
    if (!container) return { width: BASE_WIDTH, height: BASE_HEIGHT };
    const width = container.clientWidth;
    const height = container.clientHeight;
    return { width: Math.floor(width), height: Math.floor(height) };
  }

  private calculatePerspectiveScale(depthFraction: number): number {
    const d = PERSPECTIVE_CONFIG.viewerDistance;
    const courtDepth = COURT.length;
    return d / (d + depthFraction * courtDepth);
  }

  private calculateScreenPosition(depthFraction: number): number {
    const steps = 100;
    let integral = 0;
    let totalIntegral = 0;

    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      const scale = this.calculatePerspectiveScale(t);
      const contribution = scale;
      totalIntegral += contribution;
      if (t < depthFraction) {
        integral += contribution;
      }
    }

    return integral / totalIntegral;
  }

  private getPerspective(): Perspective {
    const cfg = PERSPECTIVE_CONFIG;
    const farScale = this.calculatePerspectiveScale(1.0);
    const nearScale = this.calculatePerspectiveScale(0.0);
    const scaleRatio = farScale / nearScale;

    const nearY = this.CANVAS_HEIGHT * (1 - cfg.courtMarginBottom);
    const farY = this.CANVAS_HEIGHT * cfg.courtMarginTop;
    const courtScreenHeight = nearY - farY;

    const nearHalfWidth = (cfg.nearWidthRatio * this.CANVAS_WIDTH) / 2;
    const farHalfWidth = nearHalfWidth * scaleRatio;
    const centerX = this.CANVAS_WIDTH / 2;

    const netDepthFraction = 0.5;
    const netScreenFraction = this.calculateScreenPosition(netDepthFraction);
    const netY = nearY - (courtScreenHeight * netScreenFraction);

    return {
      nearLeft: centerX - nearHalfWidth,
      nearRight: centerX + nearHalfWidth,
      nearY: nearY,
      farLeft: centerX - farHalfWidth,
      farRight: centerX + farHalfWidth,
      farY: farY,
      netY: netY,
      scaleRatio: scaleRatio
    };
  }

  private getPerspectiveX(x: number, y: number): number {
    const P = this.PERSPECTIVE;
    const t = (y - P.nearY) / (P.farY - P.nearY);
    const leftX = P.nearLeft + t * (P.farLeft - P.nearLeft);
    const rightX = P.nearRight + t * (P.farRight - P.nearRight);
    return leftX + x * (rightX - leftX);
  }

  private getCourtWidth(y: number): number {
    const P = this.PERSPECTIVE;
    const t = (y - P.nearY) / (P.farY - P.nearY);
    const leftX = P.nearLeft + t * (P.farLeft - P.nearLeft);
    const rightX = P.nearRight + t * (P.farRight - P.nearRight);
    return rightX - leftX;
  }

  private getCourtLeft(y: number): number {
    const P = this.PERSPECTIVE;
    const t = (y - P.nearY) / (P.farY - P.nearY);
    return P.nearLeft + t * (P.farLeft - P.nearLeft);
  }

  private getScaleFactor(y: number): number {
    const P = this.PERSPECTIVE;
    const t = (y - P.nearY) / (P.farY - P.nearY);
    return 1 - t * 0.5;
  }

  private drawBackground(): void {
    const bg = new Konva.Rect({
      x: 0,
      y: 0,
      width: this.CANVAS_WIDTH,
      height: this.CANVAS_HEIGHT,
      fill: '#1a3a4a'
    });
    this.backgroundLayer.add(bg);
    // No title bar - using HTML overlay for description
  }

  private drawCourt(): void {
    const P = this.PERSPECTIVE;
    const singlesInset = 0.09;

    // Dark navy surround
    const surroundPadding = 0.15;
    const surroundNearLeft = P.nearLeft - (P.nearRight - P.nearLeft) * surroundPadding;
    const surroundNearRight = P.nearRight + (P.nearRight - P.nearLeft) * surroundPadding;
    const surroundFarLeft = P.farLeft - (P.farRight - P.farLeft) * surroundPadding;
    const surroundFarRight = P.farRight + (P.farRight - P.farLeft) * surroundPadding;
    const surroundNearY = P.nearY + 30;
    const surroundFarY = P.farY - 15;

    const surround = new Konva.Line({
      points: [
        surroundNearLeft, surroundNearY,
        surroundNearRight, surroundNearY,
        surroundFarRight, surroundFarY,
        surroundFarLeft, surroundFarY
      ],
      fill: '#1a2a4a',
      closed: true
    });
    this.courtLayer.add(surround);

    // Court surface
    const surface = new Konva.Line({
      points: [
        P.nearLeft, P.nearY,
        P.nearRight, P.nearY,
        P.farRight, P.farY,
        P.farLeft, P.farY
      ],
      fill: '#5B9BD5',
      closed: true
    });
    this.courtLayer.add(surface);

    // Outer boundary
    const boundary = new Konva.Line({
      points: [
        P.nearLeft, P.nearY,
        P.nearRight, P.nearY,
        P.farRight, P.farY,
        P.farLeft, P.farY
      ],
      stroke: 'white',
      strokeWidth: 3,
      closed: true
    });
    this.courtLayer.add(boundary);

    // Singles sidelines
    const leftSingles = new Konva.Line({
      points: [
        this.getPerspectiveX(singlesInset, P.nearY), P.nearY,
        this.getPerspectiveX(singlesInset, P.farY), P.farY
      ],
      stroke: 'white',
      strokeWidth: 2
    });
    this.courtLayer.add(leftSingles);

    const rightSingles = new Konva.Line({
      points: [
        this.getPerspectiveX(1 - singlesInset, P.nearY), P.nearY,
        this.getPerspectiveX(1 - singlesInset, P.farY), P.farY
      ],
      stroke: 'white',
      strokeWidth: 2
    });
    this.courtLayer.add(rightSingles);

    // Net
    const netLeft = this.getCourtLeft(P.netY);
    const netWidth = this.getCourtWidth(P.netY);
    const netRight = netLeft + netWidth;
    const netCenter = netLeft + netWidth / 2;
    const postHeight = 18;
    const centerHeight = 10;
    const postWidth = 4;

    const netMesh = new Konva.Shape({
      sceneFunc: (ctx, shape) => {
        ctx.beginPath();
        ctx.moveTo(netLeft, P.netY);
        ctx.lineTo(netRight, P.netY);
        ctx.lineTo(netRight, P.netY - postHeight);
        ctx.quadraticCurveTo(netCenter, P.netY - centerHeight, netLeft, P.netY - postHeight);
        ctx.closePath();
        ctx.fillStrokeShape(shape);
      },
      fill: 'rgba(255, 255, 255, 0.35)',
      stroke: 'rgba(255, 255, 255, 0.6)',
      strokeWidth: 1
    });
    this.courtLayer.add(netMesh);

    const netTopBand = new Konva.Shape({
      sceneFunc: (ctx, shape) => {
        ctx.beginPath();
        ctx.moveTo(netLeft, P.netY - postHeight);
        ctx.quadraticCurveTo(netCenter, P.netY - centerHeight, netRight, P.netY - postHeight);
        ctx.strokeShape(shape);
      },
      stroke: 'white',
      strokeWidth: 2
    });
    this.courtLayer.add(netTopBand);

    // Posts
    const leftPost = new Konva.Rect({
      x: netLeft - postWidth/2,
      y: P.netY - postHeight,
      width: postWidth,
      height: postHeight,
      fill: 'white'
    });
    this.courtLayer.add(leftPost);

    const rightPost = new Konva.Rect({
      x: netRight - postWidth/2,
      y: P.netY - postHeight,
      width: postWidth,
      height: postHeight,
      fill: 'white'
    });
    this.courtLayer.add(rightPost);

    // Service lines
    const nearServiceDepth = (COURT.halfCourt - COURT.serviceLineFromNet) / COURT.length;
    const farServiceDepth = (COURT.halfCourt + COURT.serviceLineFromNet) / COURT.length;

    const nearServiceScreenPos = this.calculateScreenPosition(nearServiceDepth);
    const farServiceScreenPos = this.calculateScreenPosition(farServiceDepth);

    const courtScreenHeight = P.nearY - P.farY;
    const serviceLineNearY = P.nearY - (courtScreenHeight * nearServiceScreenPos);
    const serviceLineFarY = P.nearY - (courtScreenHeight * farServiceScreenPos);

    // Near service line
    const nearServiceLeft = this.getPerspectiveX(singlesInset, serviceLineNearY);
    const nearServiceRight = this.getPerspectiveX(1 - singlesInset, serviceLineNearY);
    const nearServiceLine = new Konva.Line({
      points: [nearServiceLeft, serviceLineNearY, nearServiceRight, serviceLineNearY],
      stroke: 'white',
      strokeWidth: 2
    });
    this.courtLayer.add(nearServiceLine);

    // Far service line
    const farServiceLeft = this.getPerspectiveX(singlesInset, serviceLineFarY);
    const farServiceRight = this.getPerspectiveX(1 - singlesInset, serviceLineFarY);
    const farServiceLine = new Konva.Line({
      points: [farServiceLeft, serviceLineFarY, farServiceRight, serviceLineFarY],
      stroke: 'white',
      strokeWidth: 2
    });
    this.courtLayer.add(farServiceLine);

    // Center service lines
    const centerServiceNear = new Konva.Line({
      points: [
        this.getPerspectiveX(0.5, serviceLineNearY), serviceLineNearY,
        this.getPerspectiveX(0.5, P.netY), P.netY
      ],
      stroke: 'white',
      strokeWidth: 2
    });
    this.courtLayer.add(centerServiceNear);

    const centerServiceFar = new Konva.Line({
      points: [
        this.getPerspectiveX(0.5, P.netY), P.netY,
        this.getPerspectiveX(0.5, serviceLineFarY), serviceLineFarY
      ],
      stroke: 'white',
      strokeWidth: 2
    });
    this.courtLayer.add(centerServiceFar);

    // Center marks
    const nearCenterMark = new Konva.Line({
      points: [
        this.getPerspectiveX(0.5, P.nearY), P.nearY,
        this.getPerspectiveX(0.5, P.nearY - 15), P.nearY - 15
      ],
      stroke: 'white',
      strokeWidth: 3
    });
    this.courtLayer.add(nearCenterMark);

    const farCenterMark = new Konva.Line({
      points: [
        this.getPerspectiveX(0.5, P.farY), P.farY,
        this.getPerspectiveX(0.5, P.farY + 8), P.farY + 8
      ],
      stroke: 'white',
      strokeWidth: 2
    });
    this.courtLayer.add(farCenterMark);
  }

  private createTitleText(): void {
    const title = new Konva.Text({
      id: 'title-text',
      x: this.CANVAS_WIDTH / 2,
      y: 25,
      text: this.titleInput.value,
      fontSize: 16,
      fontStyle: '500',
      fill: 'white',
      fontFamily: '-apple-system, Helvetica Neue, Arial, sans-serif'
    });
    title.x(this.CANVAS_WIDTH / 2 - title.width() / 2);

    title.on('click tap', () => {
      this.editText(title, this.uiLayer, true);
    });

    this.uiLayer.add(title);
  }

  private createDefaultPlayers(): void {
    const P = this.PERSPECTIVE;
    const nearY = P.nearY - (this.CANVAS_HEIGHT * 0.04);
    const farY = P.farY + (this.CANVAS_HEIGHT * 0.03);

    this.createPlayer(this.getPerspectiveX(0.70, nearY), nearY, '#22C55E', 'You');
    this.createPlayer(this.getPerspectiveX(0.30, farY), farY, '#EF4444', '');
  }

  private createPlayer(x: number, y: number, color: string, label: string): Konva.Group {
    const scale = this.getScaleFactor(y);
    const baseRadiusX = 22;
    const baseRadiusY = 8;

    const group = new Konva.Group({
      x: x,
      y: y,
      draggable: true,
      name: 'player'
    });

    const ellipse = new Konva.Ellipse({
      radiusX: baseRadiusX * scale,
      radiusY: baseRadiusY * scale,
      fill: color,
      stroke: '#fff',
      strokeWidth: 2 * scale
    });
    group.add(ellipse);

    const fontSize = Math.max(12, 14 * scale);
    // Always place label below the player ellipse
    const labelOffsetY = (baseRadiusY + 8) * scale;

    const text = new Konva.Text({
      text: label,
      fontSize: fontSize,
      fill: 'white',
      fontFamily: 'Arial',
      y: labelOffsetY,
      align: 'center',
      name: 'player-label'
    });
    text.x(-text.width() / 2);

    const hitBg = new Konva.Rect({
      x: text.x() - 10,
      y: text.y() - 4,
      width: text.width() + 20,
      height: text.height() + 8,
      fill: 'transparent',
      name: 'label-hitarea'
    });

    const editLabel = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      e.cancelBubble = true;
      const absPos = text.getAbsolutePosition();
      this.editPlayerLabel(text, group, absPos);
    };
    text.on('click tap', editLabel);
    hitBg.on('click tap', editLabel);

    group.add(hitBg);
    group.add(text);

    group.on('dragmove', () => {
      const newScale = this.getScaleFactor(group.y());
      const e = group.findOne('Ellipse') as Konva.Ellipse;
      const t = group.findOne('Text') as Konva.Text;
      const h = group.findOne('.label-hitarea') as Konva.Rect;
      if (e) {
        e.radiusX(baseRadiusX * newScale);
        e.radiusY(baseRadiusY * newScale);
        e.strokeWidth(2 * newScale);
      }
      if (t) {
        const newFontSize = Math.max(12, 14 * newScale);
        t.fontSize(newFontSize);
        // Always place label below
        const labelOffsetY = (baseRadiusY + 8) * newScale;
        t.y(labelOffsetY);
        t.x(-t.width() / 2);
        if (h) {
          h.x(t.x() - 10);
          h.y(t.y() - 4);
          h.width(t.width() + 20);
          h.height(t.height() + 8);
        }
      }
    });

    // Trash icon (always above the player)
    const trashGroup = new Konva.Group({
      x: 0,
      y: -(baseRadiusY + 35),
      visible: false,
      name: 'trash-icon'
    });

    const trashBg = new Konva.Circle({
      radius: 12,
      fill: '#EF4444',
      stroke: 'white',
      strokeWidth: 2
    });
    trashGroup.add(trashBg);

    const trashIcon = new Konva.Shape({
      sceneFunc: (ctx, shape) => {
        ctx.beginPath();
        ctx.moveTo(-5, -5);
        ctx.lineTo(5, -5);
        ctx.moveTo(-4, -3);
        ctx.lineTo(-3, 5);
        ctx.lineTo(3, 5);
        ctx.lineTo(4, -3);
        ctx.moveTo(-2, -5);
        ctx.lineTo(-2, -7);
        ctx.lineTo(2, -7);
        ctx.lineTo(2, -5);
        ctx.strokeShape(shape);
      },
      stroke: 'white',
      strokeWidth: 1.5,
      lineCap: 'round',
      lineJoin: 'round'
    });
    trashGroup.add(trashIcon);

    trashGroup.on('click tap', (e) => {
      e.cancelBubble = true;
      group.destroy();
      this.selectedShape = null;
      this.playerLayer.draw();
      this.saveState();
    });

    group.add(trashGroup);

    group.on('click tap', (e) => {
      if (this.currentMode === 'select') {
        this.selectShape(group);
        e.cancelBubble = true;
      }
    });

    group.on('dragstart', () => {
      this.selectShape(group);
    });

    group.on('dragend', () => {
      this.saveState();
    });

    this.playerLayer.add(group);
    this.playerLayer.draw();
    return group;
  }

  private createArrow(
    initStartX: number,
    initStartY: number,
    initEndX: number,
    initEndY: number,
    color: string,
    style = 'curved',
    customCtrl: { x: number; y: number } | null = null,
    initBounce: { x: number; y: number } | null = null,
    hitNumber: number | null = null
  ): Konva.Group {
    let startX = initStartX;
    let startY = initStartY;
    let endX = initEndX;
    let endY = initEndY;

    const group = new Konva.Group({ name: 'arrow' });

    // Calculate bounce point and arc heights
    const calcBounceParams = () => {
      let bounceX: number, bounceY: number;

      if (initBounce) {
        bounceX = initBounce.x;
        bounceY = initBounce.y;
      } else {
        const dx = endX - startX;
        const dy = endY - startY;
        const bounceRatio = 0.65;
        bounceX = startX + dx * bounceRatio;
        bounceY = startY + dy * bounceRatio;
      }

      const dx1 = bounceX - startX;
      const dy1 = bounceY - startY;
      const distance1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);

      const dx2 = endX - bounceX;
      const dy2 = endY - bounceY;
      const distance2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

      const arc1Height = distance1 * 0.22;
      const arc2Height = distance2 * 0.3;

      const perpX = distance1 > 0 ? -dy1 / distance1 : 0;
      const perpY = distance1 > 0 ? dx1 / distance1 : 0;

      let arcDirection: number;
      if (Math.abs(perpY) > 0.1) {
        arcDirection = perpY < 0 ? 1 : -1;
      } else {
        const midX = (startX + bounceX) / 2;
        const courtCenterX = this.CANVAS_WIDTH / 2;
        arcDirection = midX < courtCenterX ? -1 : 1;
      }

      const mid1X = (startX + bounceX) / 2;
      const mid1Y = (startY + bounceY) / 2;
      const ctrl1X = mid1X + perpX * arc1Height * arcDirection;
      const ctrl1Y = mid1Y + perpY * arc1Height * arcDirection;

      const perp2X = distance2 > 0 ? -dy2 / distance2 : 0;
      const perp2Y = distance2 > 0 ? dx2 / distance2 : 0;
      const mid2X = (bounceX + endX) / 2;
      const mid2Y = (bounceY + endY) / 2;
      const ctrl2X = mid2X + perp2X * arc2Height * arcDirection;
      const ctrl2Y = mid2Y + perp2Y * arc2Height * arcDirection;

      return { bounceX, bounceY, ctrl1X, ctrl1Y, ctrl2X, ctrl2Y, arcDirection };
    };

    let bounceParams = calcBounceParams();

    let arc1Apex = { t: 0.5, offset: null as number | null };

    if (customCtrl) {
      bounceParams.ctrl1X = customCtrl.x;
      bounceParams.ctrl1Y = customCtrl.y;
    }

    const computeApex = (
      baseStartX: number, baseStartY: number,
      baseEndX: number, baseEndY: number,
      t: number, offset: number | null, defaultOffsetRatio: number
    ) => {
      const dx = baseEndX - baseStartX;
      const dy = baseEndY - baseStartY;
      const len = Math.sqrt(dx * dx + dy * dy);

      if (len < 1) return { x: (baseStartX + baseEndX) / 2, y: (baseStartY + baseEndY) / 2 };

      const baseX = baseStartX + dx * t;
      const baseY = baseStartY + dy * t;

      const perpX = -dy / len;
      const perpY = dx / len;

      let actualOffset: number;
      if (offset !== null) {
        actualOffset = offset;
      } else {
        const midX = (baseStartX + baseEndX) / 2;
        const courtCenterX = this.CANVAS_WIDTH / 2;
        const sign = midX < courtCenterX ? -1 : 1;
        actualOffset = len * defaultOffsetRatio * sign;
      }

      return {
        x: baseX + perpX * actualOffset,
        y: baseY + perpY * actualOffset
      };
    };

    const extractApexRelative = (
      baseStartX: number, baseStartY: number,
      baseEndX: number, baseEndY: number,
      apexX: number, apexY: number
    ) => {
      const dx = baseEndX - baseStartX;
      const dy = baseEndY - baseStartY;
      const len = Math.sqrt(dx * dx + dy * dy);

      if (len < 1) return { t: 0.5, offset: 0 };

      const toApexX = apexX - baseStartX;
      const toApexY = apexY - baseStartY;
      const t = Math.max(0, Math.min(1, (toApexX * dx + toApexY * dy) / (len * len)));

      const baseX = baseStartX + dx * t;
      const baseY = baseStartY + dy * t;

      const perpX = -dy / len;
      const perpY = dx / len;

      const toApexFromBaseX = apexX - baseX;
      const toApexFromBaseY = apexY - baseY;
      const offset = toApexFromBaseX * perpX + toApexFromBaseY * perpY;

      return { t, offset };
    };

    const recomputeArrow = () => {
      const bp = bounceParams;

      const dx1 = bp.bounceX - startX;
      const dy1 = bp.bounceY - startY;
      const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);

      if (len1 < 1) return;

      const apex1 = computeApex(startX, startY, bp.bounceX, bp.bounceY, arc1Apex.t, arc1Apex.offset, 0.22);
      bp.ctrl1X = apex1.x;
      bp.ctrl1Y = apex1.y;

      const perp1X = -dy1 / len1;
      const perp1Y = dx1 / len1;
      const mid1X = (startX + bp.bounceX) / 2;
      const mid1Y = (startY + bp.bounceY) / 2;
      const ctrl1OffsetX = bp.ctrl1X - mid1X;
      const ctrl1OffsetY = bp.ctrl1Y - mid1Y;
      const arc1Side = (ctrl1OffsetX * perp1X + ctrl1OffsetY * perp1Y) >= 0 ? 1 : -1;

      const secondArcLen = len1 * 0.25;
      endX = bp.bounceX + (dx1 / len1) * secondArcLen;
      endY = bp.bounceY + (dy1 / len1) * secondArcLen;

      const dx2 = endX - bp.bounceX;
      const dy2 = endY - bp.bounceY;
      const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

      if (len2 < 1) return;

      const perp2X = -dy2 / len2;
      const perp2Y = dx2 / len2;

      const mid2X = (bp.bounceX + endX) / 2;
      const mid2Y = (bp.bounceY + endY) / 2;
      const offset2 = len2 * 0.3 * arc1Side;

      bp.ctrl2X = mid2X + perp2X * offset2;
      bp.ctrl2Y = mid2Y + perp2Y * offset2;
    };

    const generateBezierPoints = (sx: number, sy: number, ex: number, ey: number, cx: number, cy: number) => {
      const pts: number[] = [];
      for (let t = 0; t <= 1; t += 0.05) {
        const x = (1 - t) * (1 - t) * sx + 2 * (1 - t) * t * cx + t * t * ex;
        const y = (1 - t) * (1 - t) * sy + 2 * (1 - t) * t * cy + t * t * ey;
        pts.push(x, y);
      }
      return pts;
    };

    const generateBounceTrajectory = () => {
      const bp = bounceParams;
      const arc1 = generateBezierPoints(startX, startY, bp.bounceX, bp.bounceY, bp.ctrl1X, bp.ctrl1Y);
      const arc2 = generateBezierPoints(bp.bounceX, bp.bounceY, endX, endY, bp.ctrl2X, bp.ctrl2Y);
      return [...arc1, ...arc2];
    };

    const updateArrowHead = (pts: number[]) => {
      const lastIdx = pts.length;
      const arrowEndX = pts[lastIdx - 2];
      const arrowEndY = pts[lastIdx - 1];
      const prevX = pts[lastIdx - 4] || startX;
      const prevY = pts[lastIdx - 3] || startY;

      const angle = Math.atan2(arrowEndY - prevY, arrowEndX - prevX);
      const headLength = 15;
      const headAngle = Math.PI / 6;

      return [
        arrowEndX - headLength * Math.cos(angle - headAngle),
        arrowEndY - headLength * Math.sin(angle - headAngle),
        arrowEndX,
        arrowEndY,
        arrowEndX - headLength * Math.cos(angle + headAngle),
        arrowEndY - headLength * Math.sin(angle + headAngle)
      ];
    };

    let points = style === 'curved' ? generateBounceTrajectory() : [startX, startY, endX, endY];

    const line = new Konva.Line({
      points: points,
      stroke: color,
      strokeWidth: 4,
      hitStrokeWidth: 20,
      lineCap: 'round',
      lineJoin: 'round',
      name: 'arrow-line'
    });
    group.add(line);

    const arrowHead = new Konva.Line({
      points: updateArrowHead(points),
      stroke: color,
      strokeWidth: 4,
      lineCap: 'round',
      lineJoin: 'round',
      name: 'arrow-head'
    });
    group.add(arrowHead);

    // Hit number label
    if (hitNumber !== null) {
      const hitLabel = new Konva.Label({
        x: startX - 20,
        y: startY - 20,
        name: 'hit-label'
      });

      hitLabel.add(new Konva.Tag({
        fill: color,
        cornerRadius: 10
      }));

      hitLabel.add(new Konva.Text({
        text: String(hitNumber),
        fontSize: 14,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontStyle: 'bold',
        fill: 'white',
        padding: 4,
        align: 'center'
      }));

      group.add(hitLabel);
    }

    const redrawArrow = () => {
      recomputeArrow();

      const newPoints = style === 'curved' ? generateBounceTrajectory() : [startX, startY, endX, endY];
      line.points(newPoints);
      arrowHead.points(updateArrowHead(newPoints));

      const bHandle = group.findOne('.bounce-handle') as Konva.Circle;
      if (bHandle) {
        bHandle.x(bounceParams.bounceX);
        bHandle.y(bounceParams.bounceY);
      }

      const hHandle = group.findOne('.height-handle') as Konva.Circle;
      if (hHandle) {
        hHandle.x(bounceParams.ctrl1X);
        hHandle.y(bounceParams.ctrl1Y);
      }

      const trash = group.findOne('.trash-icon') as Konva.Group;
      if (trash) {
        trash.x(startX);
        trash.y(startY - 45);
      }

      const label = group.findOne('.hit-label') as Konva.Label;
      if (label) {
        label.x(startX - 20);
        label.y(startY - 20);
      }

      group.setAttrs({
        arrowData: { startX, startY, endX, endY, color, style, bounce: { x: bounceParams.bounceX, y: bounceParams.bounceY } }
      });
      this.arrowLayer.batchDraw();
    };

    // Start handle
    const startHandle = new Konva.Circle({
      x: startX,
      y: startY,
      radius: 8,
      fill: 'white',
      stroke: color,
      strokeWidth: 2,
      draggable: true,
      visible: false,
      name: 'start-handle'
    });

    startHandle.on('dragmove', () => {
      startX = startHandle.x();
      startY = startHandle.y();
      redrawArrow();
    });

    startHandle.on('dragend', () => { this.arrowLayer.draw(); this.saveState(); });
    group.add(startHandle);

    // Bounce handle
    const bounceHandle = new Konva.Circle({
      x: bounceParams.bounceX,
      y: bounceParams.bounceY,
      radius: 8,
      fill: 'white',
      stroke: color,
      strokeWidth: 2,
      draggable: true,
      visible: false,
      name: 'bounce-handle'
    });

    bounceHandle.on('dragmove', () => {
      const bp = bounceParams;
      bp.bounceX = bounceHandle.x();
      bp.bounceY = bounceHandle.y();
      recomputeArrow();

      const hHandle = group.findOne('.height-handle') as Konva.Circle;
      if (hHandle) {
        hHandle.x(bp.ctrl1X);
        hHandle.y(bp.ctrl1Y);
      }

      const newPoints = generateBounceTrajectory();
      line.points(newPoints);
      arrowHead.points(updateArrowHead(newPoints));
      this.arrowLayer.batchDraw();
    });

    bounceHandle.on('dragend', () => { this.arrowLayer.draw(); this.saveState(); });
    group.add(bounceHandle);

    // Height handle
    const heightHandle = new Konva.Circle({
      x: bounceParams.ctrl1X,
      y: bounceParams.ctrl1Y,
      radius: 8,
      fill: 'white',
      stroke: color,
      strokeWidth: 2,
      draggable: true,
      visible: false,
      name: 'height-handle'
    });

    heightHandle.on('dragmove', () => {
      bounceParams.ctrl1X = heightHandle.x();
      bounceParams.ctrl1Y = heightHandle.y();

      const relative = extractApexRelative(
        startX, startY,
        bounceParams.bounceX, bounceParams.bounceY,
        bounceParams.ctrl1X, bounceParams.ctrl1Y
      );
      arc1Apex.t = relative.t;
      arc1Apex.offset = relative.offset;

      const bp = bounceParams;
      const mid2X = (bp.bounceX + endX) / 2;
      const mid2Y = (bp.bounceY + endY) / 2;

      const mid1X = (startX + bp.bounceX) / 2;
      const mid1Y = (startY + bp.bounceY) / 2;
      const dirX = bp.ctrl1X - mid1X;
      const dirY = bp.ctrl1Y - mid1Y;
      const dirLen = Math.sqrt(dirX * dirX + dirY * dirY);

      if (dirLen > 0) {
        const secondArcLen = Math.sqrt(Math.pow(endX - bp.bounceX, 2) + Math.pow(endY - bp.bounceY, 2));
        const arc2Height = secondArcLen * 0.3;
        bp.ctrl2X = mid2X + (dirX / dirLen) * arc2Height;
        bp.ctrl2Y = mid2Y + (dirY / dirLen) * arc2Height;
      }

      const newPoints = generateBounceTrajectory();
      line.points(newPoints);
      arrowHead.points(updateArrowHead(newPoints));
      this.arrowLayer.batchDraw();
    });

    heightHandle.on('dragend', () => { this.arrowLayer.draw(); this.saveState(); });
    group.add(heightHandle);

    // Trash icon
    const trashGroup = new Konva.Group({
      x: startX,
      y: startY - 45,
      visible: false,
      name: 'trash-icon'
    });

    const trashBg = new Konva.Circle({
      radius: 14,
      fill: '#EF4444',
      stroke: 'white',
      strokeWidth: 2
    });
    trashGroup.add(trashBg);

    const trashIcon = new Konva.Shape({
      sceneFunc: (ctx, shape) => {
        ctx.beginPath();
        ctx.moveTo(-6, -6);
        ctx.lineTo(6, -6);
        ctx.moveTo(-5, -4);
        ctx.lineTo(-4, 6);
        ctx.lineTo(4, 6);
        ctx.lineTo(5, -4);
        ctx.moveTo(-2, -6);
        ctx.lineTo(-2, -8);
        ctx.lineTo(2, -8);
        ctx.lineTo(2, -6);
        ctx.strokeShape(shape);
      },
      stroke: 'white',
      strokeWidth: 1.5,
      lineCap: 'round',
      lineJoin: 'round'
    });
    trashGroup.add(trashIcon);

    trashGroup.on('click tap', (e) => {
      e.cancelBubble = true;
      group.destroy();
      this.selectedShape = null;
      this.arrowLayer.draw();
      this.saveState();
    });

    group.add(trashGroup);

    group.setAttrs({
      arrowData: { startX, startY, endX, endY, color, style, ctrl: style === 'curved' ? { x: bounceParams.ctrl1X, y: bounceParams.ctrl1Y } : null }
    });

    group.on('click tap', (e) => {
      if (this.currentMode === 'select') {
        this.selectShape(group);
        e.cancelBubble = true;
      }
    });

    this.arrowLayer.add(group);
    this.arrowLayer.draw();
    return group;
  }

  private createArrowWithBounce(
    startX: number, startY: number,
    bounceX: number, bounceY: number,
    endX: number, endY: number,
    color: string,
    style = 'curved',
    hitNumber: number | null = null
  ): Konva.Group {
    return this.createArrow(startX, startY, endX, endY, color, style, null, { x: bounceX, y: bounceY }, hitNumber);
  }

  private selectShape(shape: Konva.Group | null): void {
    if (this.selectedShape) {
      const prevEllipse = this.selectedShape.findOne('Ellipse') as Konva.Ellipse;
      if (prevEllipse) {
        prevEllipse.stroke('#fff');
        const scale = this.getScaleFactor(this.selectedShape.y());
        prevEllipse.strokeWidth(2 * scale);
      }
      const prevTrash = this.selectedShape.findOne('.trash-icon') as Konva.Group;
      if (prevTrash) prevTrash.visible(false);

      if (this.selectedShape.name() === 'arrow') {
        const prevLine = this.selectedShape.findOne('.arrow-line') as Konva.Line;
        if (prevLine) {
          prevLine.shadowEnabled(false);
        }
        const handleNames = ['.start-handle', '.bounce-handle', '.height-handle'];
        handleNames.forEach(name => {
          const handle = this.selectedShape!.findOne(name);
          if (handle) handle.visible(false);
        });
      }
    }

    this.selectedShape = shape;

    if (shape) {
      const ellipse = shape.findOne('Ellipse') as Konva.Ellipse;
      if (ellipse) {
        ellipse.stroke('#e94560');
        const scale = this.getScaleFactor(shape.y());
        ellipse.strokeWidth(4 * scale);
      }
      const trash = shape.findOne('.trash-icon') as Konva.Group;
      if (trash) trash.visible(true);

      if (shape.name() === 'arrow') {
        const line = shape.findOne('.arrow-line') as Konva.Line;
        if (line) {
          line.shadowColor('#e94560');
          line.shadowBlur(10);
          line.shadowEnabled(true);
        }
        const handleNames = ['.start-handle', '.bounce-handle', '.height-handle'];
        handleNames.forEach(name => {
          const handle = shape.findOne(name);
          if (handle) handle.visible(true);
        });
      }
    }

    this.playerLayer.draw();
    this.arrowLayer.draw();
  }

  private startDrawing(pos: { x: number; y: number }): void {
    this.isDrawing = true;
    this.drawStartPos = pos;
  }

  private continueDrawing(pos: { x: number; y: number }): void {
    if (!this.isDrawing || !this.drawStartPos) return;

    const dx = pos.x - this.drawStartPos.x;
    const dy = pos.y - this.drawStartPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 15) return;

    if (this.tempArrow) {
      this.tempArrow.destroy();
    }
    const previewColor = this.drawStartPos.y < pos.y ? '#EF4444' : '#22C55E';

    const bounceX = pos.x;
    const bounceY = pos.y;
    const secondArcLen = distance * 0.25;
    const endX = bounceX + (dx / distance) * secondArcLen;
    const endY = bounceY + (dy / distance) * secondArcLen;

    this.tempArrow = this.createArrowWithBounce(this.drawStartPos.x, this.drawStartPos.y, bounceX, bounceY, endX, endY, previewColor, this.currentArrowStyle);
    this.tempArrow.opacity(0.5);
    const handleNames = ['.start-handle', '.bounce-handle', '.height-handle'];
    handleNames.forEach(name => {
      const handle = this.tempArrow!.findOne(name);
      if (handle) handle.visible(false);
    });
    this.arrowLayer.draw();
  }

  private endDrawing(pos: { x: number; y: number }): void {
    if (!this.isDrawing || !this.drawStartPos) return;

    this.isDrawing = false;

    if (this.tempArrow) {
      this.tempArrow.destroy();
      this.tempArrow = null;
    }

    const dx = pos.x - this.drawStartPos.x;
    const dy = pos.y - this.drawStartPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > 20) {
      const arrowColor = this.drawStartPos.y < pos.y ? '#EF4444' : '#22C55E';

      const bounceX = pos.x;
      const bounceY = pos.y;
      const secondArcLen = distance * 0.25;
      const endX = bounceX + (dx / distance) * secondArcLen;
      const endY = bounceY + (dy / distance) * secondArcLen;

      this.hitCounter++;

      const newArrow = this.createArrowWithBounce(this.drawStartPos.x, this.drawStartPos.y, bounceX, bounceY, endX, endY, arrowColor, this.currentArrowStyle, this.hitCounter);
      this.selectShape(newArrow);
      this.saveState();
    }

    this.arrowLayer.draw();
  }

  private bindEvents(): void {
    // Stage events
    this.stage.on('mousedown touchstart', (e) => {
      const target = e.target;
      const parent = target.parent;

      const isOnPlayer = parent && parent.name() === 'player';
      const isOnArrow = parent && parent.name() === 'arrow';
      const isOnHandle = target.name() && (target.name().includes('handle') || target.name().includes('trash'));

      if (!isOnPlayer && !isOnArrow && !isOnHandle &&
          (target === this.stage || parent === this.courtLayer || parent === this.backgroundLayer)) {
        this.selectShape(null);
        const pos = this.stage.getPointerPosition();
        if (pos) this.startDrawing(pos);
      }
    });

    this.stage.on('mousemove touchmove', () => {
      if (this.isDrawing) {
        const pos = this.stage.getPointerPosition();
        if (pos) this.continueDrawing(pos);
      }
    });

    this.stage.on('mouseup touchend', () => {
      if (this.isDrawing) {
        const pos = this.stage.getPointerPosition();
        if (pos) this.endDrawing(pos);
      }
    });

    // Toolbar buttons
    this.undoBtn.addEventListener('click', () => this.undo());
    this.redoBtn.addEventListener('click', () => this.redo());

    // Help modal
    this.element.querySelector('#help-btn')!.addEventListener('click', () => {
      this.helpModal.classList.add('visible');
    });

    this.element.querySelector('#help-close')!.addEventListener('click', () => {
      this.helpModal.classList.remove('visible');
    });

    this.helpModal.addEventListener('click', (e) => {
      if (e.target === this.helpModal) {
        this.helpModal.classList.remove('visible');
      }
    });

    // Pagination
    this.prevSlideBtn.addEventListener('click', () => this.goToPrevSlide());
    this.nextSlideBtn.addEventListener('click', () => this.goToNextSlide());
    this.addSlideBtn.addEventListener('click', () => this.addNewSlide());
    this.deleteSlideBtn.addEventListener('click', () => this.deleteCurrentSlide());

    // Audio controls (media bar)
    this.recordBtn.addEventListener('click', () => this.toggleRecording());
    this.playAllBtn.addEventListener('click', () => this.togglePlayback());

    // Description auto-save (contenteditable)
    this.descriptionInput.addEventListener('input', () => {
      // Save description to current slide
      if (this.slides.length > 0) {
        this.slides[this.currentSlideIndex].description = this.descriptionInput.innerText;
      }
      this.saveToLocalStorage();
      this.syncToServer();
    });

    // Handle paste to strip formatting
    this.descriptionInput.addEventListener('paste', (e) => {
      e.preventDefault();
      const text = e.clipboardData?.getData('text/plain') || '';
      document.execCommand('insertText', false, text);
    });

    // Keyboard
    document.addEventListener('keydown', this.boundHandleKeydown);

    // Resize
    window.addEventListener('resize', this.boundHandleResize);
  }

  private handleKeydown(e: KeyboardEvent): void {
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.isContentEditable) return;

    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      this.undo();
    }

    if ((e.ctrlKey || e.metaKey) && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
      e.preventDefault();
      this.redo();
    }

    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (this.selectedShape) {
        this.selectedShape.destroy();
        this.selectedShape = null;
        this.playerLayer.draw();
        this.arrowLayer.draw();
        this.saveState();
      }
    }

    if (e.key === 'Escape') {
      this.selectShape(null);
      if (this.isDrawing) {
        this.isDrawing = false;
        if (this.tempArrow) {
          this.tempArrow.destroy();
          this.tempArrow = null;
          this.arrowLayer.draw();
        }
      }
      if (this.helpModal.classList.contains('visible')) {
        this.helpModal.classList.remove('visible');
      }
    }

    // Slide navigation with arrow keys
    if (e.key === 'ArrowLeft' && !e.ctrlKey && !e.metaKey) {
      this.goToPrevSlide();
    }
    if (e.key === 'ArrowRight' && !e.ctrlKey && !e.metaKey) {
      this.goToNextSlide();
    }
  }

  private handleResize(): void {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = window.setTimeout(() => {
      const players: { xRatio: number; yRatio: number; color: string; label: string }[] = [];
      const arrows: { startXRatio: number; startYRatio: number; endXRatio: number; endYRatio: number; color: string; style: string; ctrlXRatio: number | null; ctrlYRatio: number | null }[] = [];

      this.playerLayer.find('.player').forEach((player) => {
        const p = player as Konva.Group;
        const ellipse = p.findOne('Ellipse') as Konva.Ellipse;
        const text = p.findOne('Text') as Konva.Text;
        if (ellipse && text) {
          players.push({
            xRatio: p.x() / this.CANVAS_WIDTH,
            yRatio: p.y() / this.CANVAS_HEIGHT,
            color: ellipse.fill() as string,
            label: text.text()
          });
        }
      });

      this.arrowLayer.find('.arrow').forEach((arrow) => {
        const data = arrow.getAttr('arrowData') as ArrowData;
        if (data) {
          arrows.push({
            startXRatio: data.startX / this.CANVAS_WIDTH,
            startYRatio: data.startY / this.CANVAS_HEIGHT,
            endXRatio: data.endX / this.CANVAS_WIDTH,
            endYRatio: data.endY / this.CANVAS_HEIGHT,
            color: data.color,
            style: data.style,
            ctrlXRatio: data.ctrl ? data.ctrl.x / this.CANVAS_WIDTH : null,
            ctrlYRatio: data.ctrl ? data.ctrl.y / this.CANVAS_HEIGHT : null
          });
        }
      });

      this.playerLayer.destroyChildren();
      this.arrowLayer.destroyChildren();

      const size = this.getCanvasSize();
      this.CANVAS_WIDTH = size.width;
      this.CANVAS_HEIGHT = size.height;
      this.PERSPECTIVE = this.getPerspective();

      this.stage.width(this.CANVAS_WIDTH);
      this.stage.height(this.CANVAS_HEIGHT);

      this.backgroundLayer.destroyChildren();
      this.courtLayer.destroyChildren();
      this.uiLayer.destroyChildren();

      this.drawBackground();
      this.drawCourt();

      players.forEach(p => {
        this.createPlayer(p.xRatio * this.CANVAS_WIDTH, p.yRatio * this.CANVAS_HEIGHT, p.color, p.label);
      });

      arrows.forEach(a => {
        const ctrl = (a.ctrlXRatio !== null && a.ctrlYRatio !== null)
          ? { x: a.ctrlXRatio * this.CANVAS_WIDTH, y: a.ctrlYRatio * this.CANVAS_HEIGHT }
          : null;
        this.createArrow(
          a.startXRatio * this.CANVAS_WIDTH,
          a.startYRatio * this.CANVAS_HEIGHT,
          a.endXRatio * this.CANVAS_WIDTH,
          a.endYRatio * this.CANVAS_HEIGHT,
          a.color,
          a.style,
          ctrl
        );
      });

      this.stage.draw();
    }, 150);
  }

  private editText(textNode: Konva.Text, layer: Konva.Layer, centerAfterEdit = false): void {
    const textPosition = textNode.absolutePosition();
    const stageBox = this.stage.container().getBoundingClientRect();

    const input = document.createElement('input');
    input.type = 'text';
    input.value = textNode.text();
    input.style.position = 'fixed';
    input.style.left = `${stageBox.left + textPosition.x}px`;
    input.style.top = `${stageBox.top + textPosition.y - 4}px`;
    input.style.width = `${Math.max(textNode.width() + 40, 100)}px`;
    input.style.fontSize = `${textNode.fontSize()}px`;
    input.style.fontFamily = textNode.fontFamily();
    input.style.padding = '4px 8px';
    input.style.border = '2px solid #FF6B00';
    input.style.borderRadius = '2px';
    input.style.background = 'rgba(13, 40, 51, 0.95)';
    input.style.color = 'white';
    input.style.outline = 'none';
    input.style.zIndex = '1000';
    input.style.textAlign = centerAfterEdit ? 'center' : 'left';

    document.body.appendChild(input);
    input.focus();
    input.select();

    textNode.visible(false);
    layer.draw();

    const finishEdit = () => {
      const newText = input.value.trim() || textNode.text();
      textNode.text(newText);
      textNode.visible(true);

      if (centerAfterEdit) {
        textNode.x(this.CANVAS_WIDTH / 2 - textNode.width() / 2);
      }

      if (textNode.id() === 'title-text') {
        this.titleInput.value = newText;
      }

      layer.draw();
      input.remove();
    };

    input.addEventListener('blur', finishEdit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        input.blur();
      }
      if (e.key === 'Escape') {
        input.value = textNode.text();
        input.blur();
      }
    });
  }

  private editPlayerLabel(textNode: Konva.Text, group: Konva.Group, absPos: { x: number; y: number }): void {
    const stageBox = this.stage.container().getBoundingClientRect();
    const scale = this.getScaleFactor(group.y());
    const fontSize = 11 * scale;

    const input = document.createElement('input');
    input.type = 'text';
    input.value = textNode.text();
    input.style.position = 'fixed';
    input.style.left = `${stageBox.left + absPos.x - 40}px`;
    input.style.top = `${stageBox.top + absPos.y - 4}px`;
    input.style.width = '80px';
    input.style.fontSize = `${Math.max(fontSize, 11)}px`;
    input.style.fontFamily = 'Arial, sans-serif';
    input.style.padding = '4px 8px';
    input.style.border = '2px solid #FF6B00';
    input.style.borderRadius = '2px';
    input.style.background = 'rgba(13, 40, 51, 0.95)';
    input.style.color = 'white';
    input.style.outline = 'none';
    input.style.zIndex = '1000';
    input.style.textAlign = 'center';

    document.body.appendChild(input);
    input.focus();
    input.select();

    textNode.visible(false);
    this.playerLayer.draw();

    const finishEdit = () => {
      const newText = input.value.trim() || textNode.text();
      textNode.text(newText);
      textNode.visible(true);
      textNode.x(-textNode.width() / 2);
      const hitBg = group.findOne('.label-hitarea') as Konva.Rect;
      if (hitBg) {
        hitBg.x(textNode.x() - 10);
        hitBg.width(textNode.width() + 20);
      }
      this.playerLayer.draw();
      input.remove();
    };

    input.addEventListener('blur', finishEdit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        input.blur();
      }
      if (e.key === 'Escape') {
        input.value = textNode.text();
        input.blur();
      }
    });
  }

  // State management
  private saveState(): void {
    const state: State = {
      players: [],
      arrows: []
    };

    this.playerLayer.find('.player').forEach((p) => {
      const player = p as Konva.Group;
      const ellipse = player.findOne('Ellipse') as Konva.Ellipse;
      const text = player.findOne('Text') as Konva.Text;
      if (ellipse && text) {
        state.players.push({
          x: player.x(),
          y: player.y(),
          color: ellipse.fill() as string,
          label: text.text()
        });
      }
    });

    this.arrowLayer.find('.arrow').forEach((a) => {
      const data = a.getAttr('arrowData') as ArrowData;
      if (data) {
        state.arrows.push({ ...data });
      }
    });

    this.undoStack.push(JSON.stringify(state));
    if (this.undoStack.length > this.MAX_UNDO) this.undoStack.shift();
    this.redoStack.length = 0;
    this.updateUndoRedoButtons();

    // Also persist to localStorage and sync to server
    this.saveToLocalStorage();
    this.syncToServer();
  }

  private restoreState(stateJson: string): void {
    const state: State = JSON.parse(stateJson);

    this.playerLayer.find('.player').forEach(p => p.destroy());
    this.arrowLayer.find('.arrow').forEach(a => a.destroy());
    this.selectedShape = null;

    state.players.forEach(p => {
      this.createPlayer(p.x, p.y, p.color, p.label);
    });

    state.arrows.forEach(a => {
      this.createArrow(a.startX, a.startY, a.endX, a.endY, a.color, a.style, a.ctrl || null);
    });

    this.playerLayer.draw();
    this.arrowLayer.draw();
  }

  private undo(): void {
    if (this.undoStack.length < 2) return;
    const currentState = this.undoStack.pop()!;
    this.redoStack.push(currentState);
    const previousState = this.undoStack[this.undoStack.length - 1];
    this.restoreState(previousState);
    this.updateUndoRedoButtons();
  }

  private redo(): void {
    if (this.redoStack.length === 0) return;
    const nextState = this.redoStack.pop()!;
    this.undoStack.push(nextState);
    this.restoreState(nextState);
    this.updateUndoRedoButtons();
  }

  private updateUndoRedoButtons(): void {
    this.undoBtn.style.opacity = this.undoStack.length > 1 ? '1' : '0.3';
    this.redoBtn.style.opacity = this.redoStack.length > 0 ? '1' : '0.3';
  }

  // Slide Management
  private generateSlideId(): string {
    return `slide_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getCurrentCanvasState(): State {
    const state: State = {
      players: [],
      arrows: []
    };

    // Save players as ratios (0-1) for cross-device compatibility
    this.playerLayer.find('.player').forEach((p) => {
      const player = p as Konva.Group;
      const ellipse = player.findOne('Ellipse') as Konva.Ellipse;
      const text = player.findOne('Text') as Konva.Text;
      if (ellipse && text) {
        state.players.push({
          x: pixelToRatio(player.x(), this.CANVAS_WIDTH),
          y: pixelToRatio(player.y(), this.CANVAS_HEIGHT),
          color: ellipse.fill() as string,
          label: text.text()
        });
      }
    });

    // Save arrows as ratios (0-1) for cross-device compatibility
    // Note: arrowData is stored in pixels, so convert to ratios here
    this.arrowLayer.find('.arrow').forEach((a) => {
      const data = a.getAttr('arrowData') as { startX: number; startY: number; endX: number; endY: number; color: string; style: string; ctrl?: { x: number; y: number } | null; bounce?: { x: number; y: number } };
      if (data) {
        state.arrows.push({
          startX: pixelToRatio(data.startX, this.CANVAS_WIDTH),
          startY: pixelToRatio(data.startY, this.CANVAS_HEIGHT),
          endX: pixelToRatio(data.endX, this.CANVAS_WIDTH),
          endY: pixelToRatio(data.endY, this.CANVAS_HEIGHT),
          color: data.color,
          style: data.style,
          ctrl: data.ctrl ? {
            x: pixelToRatio(data.ctrl.x, this.CANVAS_WIDTH),
            y: pixelToRatio(data.ctrl.y, this.CANVAS_HEIGHT)
          } : null,
          bounce: data.bounce ? {
            x: pixelToRatio(data.bounce.x, this.CANVAS_WIDTH),
            y: pixelToRatio(data.bounce.y, this.CANVAS_HEIGHT)
          } : undefined
        });
      }
    });

    return state;
  }

  private saveCurrentSlideToMemory(): void {
    if (this.slides.length === 0) return;

    const currentSlide = this.slides[this.currentSlideIndex];
    currentSlide.canvasState = this.getCurrentCanvasState();
  }

  private loadSlideByIndex(index: number): void {
    if (index < 0 || index >= this.slides.length) return;

    // Save current slide before switching
    this.saveCurrentSlideToMemory();
    this.saveSlideDescription();

    this.currentSlideIndex = index;
    const slide = this.slides[index];

    // Load description for this slide
    this.descriptionInput.innerText = slide.description || '';

    // Clear canvas
    this.playerLayer.find('.player').forEach(p => p.destroy());
    this.arrowLayer.find('.arrow').forEach(a => a.destroy());
    this.selectedShape = null;
    this.hitCounter = 0;

    // Restore slide state - convert ratios to current canvas pixels
    slide.canvasState.players.forEach(p => {
      const x = ratioToPixel(p.x, this.CANVAS_WIDTH);
      const y = ratioToPixel(p.y, this.CANVAS_HEIGHT);
      this.createPlayer(x, y, p.color, p.label);
    });

    slide.canvasState.arrows.forEach(a => {
      this.hitCounter++;
      const startX = ratioToPixel(a.startX, this.CANVAS_WIDTH);
      const startY = ratioToPixel(a.startY, this.CANVAS_HEIGHT);
      const endX = ratioToPixel(a.endX, this.CANVAS_WIDTH);
      const endY = ratioToPixel(a.endY, this.CANVAS_HEIGHT);
      const ctrl = a.ctrl ? {
        x: ratioToPixel(a.ctrl.x, this.CANVAS_WIDTH),
        y: ratioToPixel(a.ctrl.y, this.CANVAS_HEIGHT)
      } : null;
      const bounce = a.bounce ? {
        x: ratioToPixel(a.bounce.x, this.CANVAS_WIDTH),
        y: ratioToPixel(a.bounce.y, this.CANVAS_HEIGHT)
      } : null;
      this.createArrow(startX, startY, endX, endY, a.color, a.style, ctrl, bounce, this.hitCounter);
    });

    // Reset undo/redo for this slide
    this.undoStack = [JSON.stringify(slide.canvasState)];
    this.redoStack = [];
    this.updateUndoRedoButtons();

    this.playerLayer.draw();
    this.arrowLayer.draw();

    this.updateSlideIndicator();
    this.updatePaginationButtons();
    this.updateAudioUI();
    this.saveToLocalStorage();
  }

  private addNewSlide(): void {
    // Save current slide first
    this.saveCurrentSlideToMemory();
    this.saveSlideDescription();

    // Create new blank slide with default players
    const newSlide: Slide = {
      id: this.generateSlideId(),
      description: '',
      canvasState: {
        players: this.getDefaultPlayersState(),
        arrows: []
      }
    };

    const insertAfterIndex = this.currentSlideIndex;

    // Insert after current slide
    this.slides.splice(insertAfterIndex + 1, 0, newSlide);

    // Sync to server
    this.syncClient?.sendSlideAdd(newSlide, insertAfterIndex);

    // Navigate to new slide
    this.loadSlideByIndex(insertAfterIndex + 1);
    // Clear description for new slide
    this.descriptionInput.innerText = '';
  }

  private getDefaultPlayersState(): PlayerData[] {
    // Returns players as ratios (0-1) for storage
    const P = this.PERSPECTIVE;
    const nearY = P.nearY - (this.CANVAS_HEIGHT * 0.04);
    const farY = P.farY + (this.CANVAS_HEIGHT * 0.03);

    const playerNearX = this.getPerspectiveX(0.70, nearY);
    const playerFarX = this.getPerspectiveX(0.30, farY);

    return [
      { x: pixelToRatio(playerNearX, this.CANVAS_WIDTH), y: pixelToRatio(nearY, this.CANVAS_HEIGHT), color: '#22C55E', label: 'You' },
      { x: pixelToRatio(playerFarX, this.CANVAS_WIDTH), y: pixelToRatio(farY, this.CANVAS_HEIGHT), color: '#EF4444', label: '' }
    ];
  }

  private updateSlideIndicator(): void {
    this.slideIndicator.textContent = `${this.currentSlideIndex + 1}/${this.slides.length}`;
  }

  private updatePaginationButtons(): void {
    this.prevSlideBtn.style.opacity = this.currentSlideIndex > 0 ? '1' : '0.3';
    this.nextSlideBtn.style.opacity = this.currentSlideIndex < this.slides.length - 1 ? '1' : '0.3';
  }

  private goToPrevSlide(): void {
    if (this.currentSlideIndex > 0) {
      this.loadSlideByIndex(this.currentSlideIndex - 1);
    }
  }

  private goToNextSlide(): void {
    if (this.currentSlideIndex < this.slides.length - 1) {
      this.loadSlideByIndex(this.currentSlideIndex + 1);
    }
  }

  private deleteCurrentSlide(): void {
    // Can't delete the last slide
    if (this.slides.length <= 1) return;

    const indexToDelete = this.currentSlideIndex;

    // Sync to server first
    this.syncClient?.sendSlideDelete(indexToDelete);

    // Remove the slide
    this.slides.splice(indexToDelete, 1);

    // Adjust current index
    if (this.currentSlideIndex >= this.slides.length) {
      this.currentSlideIndex = this.slides.length - 1;
    }

    // Load the new current slide
    this.reloadCurrentSlide();
    this.loadSlideDescription();
    this.updateSlideIndicator();
    this.updatePaginationButtons();
    this.saveToLocalStorage();
  }

  private loadSlideDescription(): void {
    const slide = this.slides[this.currentSlideIndex];
    if (slide) {
      this.descriptionInput.innerText = slide.description || '';
    }
  }

  // Audio segment methods
  private initAudioRecorder(): void {
    if (!this.diagramId) return;

    this.audioRecorder = new AudioRecorder({
      diagramId: this.diagramId,
      onStateChange: (state) => {
        this.isRecording = state === 'recording';
        this.updateRecordButton();

        if (state === 'uploading') {
          this.audioTimer.textContent = 'Saving...';
        }
      },
      onTimeUpdate: (seconds) => {
        this.audioTimer.textContent = this.formatTime(seconds);
      },
      onSegmentReady: (segment) => {
        // Add segment to array
        this.audioSegments.push(segment);
        this.saveToLocalStorage();
        this.syncAudioSegmentAdd(segment);
        this.renderSegmentTimeline();
        this.audioTimer.textContent = '';
      },
      onError: (error) => {
        console.error('Audio error:', error);
        this.audioTimer.textContent = '';
        alert(error);
      },
    });

    this.segmentPlayer = new SegmentPlayer({
      onPlayStateChange: (playing) => {
        this.isPlaying = playing;
        this.updatePlayButton();
      },
      onTimeUpdate: (currentTime, totalDuration, segmentIndex) => {
        this.audioTimer.textContent = `${this.formatTime(Math.floor(currentTime))} / ${this.formatTime(Math.floor(totalDuration))}`;
        this.highlightPlayingSegment(segmentIndex);
      },
      onSegmentChange: (index) => {
        this.highlightPlayingSegment(index);
      },
      onEnded: () => {
        this.audioTimer.textContent = this.formatTime(this.getTotalDuration());
        this.highlightPlayingSegment(-1);
      },
    });
  }

  private toggleRecording(): void {
    if (!this.diagramId) {
      alert('Please save the diagram first to enable recording');
      return;
    }

    if (!this.audioRecorder) {
      this.initAudioRecorder();
    }

    if (this.isRecording) {
      this.audioRecorder?.stopRecording();
    } else {
      // Stop any playing audio first
      if (this.isPlaying) {
        this.segmentPlayer?.pause();
      }
      this.audioRecorder?.startRecording();
    }
  }

  private togglePlayback(): void {
    if (this.audioSegments.length === 0) return;

    if (!this.segmentPlayer) {
      this.initAudioRecorder();
    }

    if (this.isPlaying) {
      this.segmentPlayer?.pause();
    } else {
      this.segmentPlayer?.setSegments(this.audioSegments);
      this.segmentPlayer?.play();
    }
  }

  private updateRecordButton(): void {
    if (this.isRecording) {
      this.recordBtn.classList.add('recording');
      this.recordBtn.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="none">
          <rect x="6" y="6" width="12" height="12" rx="2"/>
        </svg>
      `;
      this.recordBtn.title = 'Stop Recording';
    } else {
      this.recordBtn.classList.remove('recording');
      this.recordBtn.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
      `;
      this.recordBtn.title = 'Record Audio';
    }
  }

  private updatePlayButton(): void {
    if (this.isPlaying) {
      this.playAllBtn.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="6" y="4" width="4" height="16"/>
          <rect x="14" y="4" width="4" height="16"/>
        </svg>
      `;
      this.playAllBtn.title = 'Pause';
    } else {
      this.playAllBtn.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polygon points="5 3 19 12 5 21 5 3"/>
        </svg>
      `;
      this.playAllBtn.title = 'Play All';
    }
  }

  private renderSegmentTimeline(): void {
    if (this.audioSegments.length === 0) {
      this.segmentTimeline.innerHTML = '<span class="empty-message">No recordings yet</span>';
      this.audioTimer.textContent = '';
      return;
    }

    const totalDuration = this.getTotalDuration();
    this.segmentTimeline.innerHTML = this.audioSegments.map((seg, index) => `
      <div class="segment-item" data-segment-id="${seg.id}" data-index="${index}">
        <span class="segment-duration">${this.formatTime(seg.duration)}</span>
        <button class="segment-delete" data-segment-id="${seg.id}" title="Delete segment">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    `).join('');

    this.audioTimer.textContent = this.formatTime(totalDuration);

    // Bind click events for segments
    this.segmentTimeline.querySelectorAll('.segment-item').forEach((el) => {
      el.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        // Don't trigger if clicking delete button
        if (target.closest('.segment-delete')) return;

        const index = parseInt(el.getAttribute('data-index') || '0');
        this.playFromSegment(index);
      });
    });

    // Bind delete events
    this.segmentTimeline.querySelectorAll('.segment-delete').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const segmentId = (btn as HTMLElement).getAttribute('data-segment-id');
        if (segmentId) {
          this.deleteSegment(segmentId);
        }
      });
    });
  }

  private playFromSegment(index: number): void {
    if (!this.segmentPlayer) {
      this.initAudioRecorder();
    }

    this.segmentPlayer?.setSegments(this.audioSegments);
    this.segmentPlayer?.seekToSegment(index);
    this.segmentPlayer?.play();
  }

  private highlightPlayingSegment(index: number): void {
    this.segmentTimeline.querySelectorAll('.segment-item').forEach((el, i) => {
      if (i === index) {
        el.classList.add('playing');
      } else {
        el.classList.remove('playing');
      }
    });
  }

  private deleteSegment(segmentId: string): void {
    // Stop playback if playing
    if (this.isPlaying) {
      this.segmentPlayer?.stop();
    }

    // Delete from server
    if (this.diagramId) {
      AudioRecorder.deleteSegment(this.diagramId, segmentId);
    }

    // Remove from local array
    this.audioSegments = this.audioSegments.filter(s => s.id !== segmentId);

    // Update UI
    this.renderSegmentTimeline();
    this.saveToLocalStorage();
    this.syncAudioSegmentDelete(segmentId);
  }

  private getTotalDuration(): number {
    return this.audioSegments.reduce((sum, s) => sum + s.duration, 0);
  }

  private syncAudioSegmentAdd(segment: AudioSegment): void {
    this.syncClient?.sendAudioSegmentAdd(segment);
  }

  private syncAudioSegmentDelete(segmentId: string): void {
    this.syncClient?.sendAudioSegmentDelete(segmentId);
  }

  private formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private saveSlideDescription(): void {
    if (this.slides.length > 0) {
      this.slides[this.currentSlideIndex].description = this.descriptionInput.innerText;
    }
  }

  private saveToLocalStorage(): void {
    // Save current slide state first
    this.saveCurrentSlideToMemory();

    const diagramState: DiagramState = {
      description: this.descriptionInput.value,
      slides: this.slides,
      currentSlideIndex: this.currentSlideIndex,
      audioSegments: this.audioSegments
    };

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(diagramState));
  }

  private loadFromLocalStorage(): boolean {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (!saved) return false;

    try {
      const diagramState: DiagramState = JSON.parse(saved);

      this.slides = diagramState.slides;
      this.currentSlideIndex = diagramState.currentSlideIndex;
      this.audioSegments = diagramState.audioSegments || [];

      if (this.slides.length === 0) return false;

      // Load current slide's description and state
      const slide = this.slides[this.currentSlideIndex];
      this.descriptionInput.innerText = slide?.description || '';

      // Clear default players
      this.playerLayer.find('.player').forEach(p => p.destroy());

      // Restore slide state - convert ratios to current canvas pixels
      slide.canvasState.players.forEach(p => {
        const x = ratioToPixel(p.x, this.CANVAS_WIDTH);
        const y = ratioToPixel(p.y, this.CANVAS_HEIGHT);
        this.createPlayer(x, y, p.color, p.label);
      });

      slide.canvasState.arrows.forEach(a => {
        this.hitCounter++;
        const startX = ratioToPixel(a.startX, this.CANVAS_WIDTH);
        const startY = ratioToPixel(a.startY, this.CANVAS_HEIGHT);
        const endX = ratioToPixel(a.endX, this.CANVAS_WIDTH);
        const endY = ratioToPixel(a.endY, this.CANVAS_HEIGHT);
        const ctrl = a.ctrl ? {
          x: ratioToPixel(a.ctrl.x, this.CANVAS_WIDTH),
          y: ratioToPixel(a.ctrl.y, this.CANVAS_HEIGHT)
        } : null;
        const bounce = a.bounce ? {
          x: ratioToPixel(a.bounce.x, this.CANVAS_WIDTH),
          y: ratioToPixel(a.bounce.y, this.CANVAS_HEIGHT)
        } : null;
        this.createArrow(startX, startY, endX, endY, a.color, a.style, ctrl, bounce, this.hitCounter);
      });

      this.updateSlideIndicator();
      this.updatePaginationButtons();
      this.renderSegmentTimeline();

      return true;
    } catch {
      return false;
    }
  }

  private initializeSlides(): void {
    // If we have a diagramId, connect to WebSocket for sync
    if (this.diagramId) {
      this.connectToSync();
      return;
    }

    // Otherwise, try to load from localStorage
    if (this.loadFromLocalStorage()) {
      return;
    }

    // Create first slide with default state
    const firstSlide: Slide = {
      id: this.generateSlideId(),
      description: '',
      canvasState: this.getCurrentCanvasState()
    };

    this.slides = [firstSlide];
    this.currentSlideIndex = 0;
    this.descriptionInput.innerText = '';
    this.updateSlideIndicator();
    this.updatePaginationButtons();
  }

  private async connectToSync(): Promise<void> {
    if (!this.diagramId) return;

    this.syncClient = new SyncClient(this.diagramId);

    // Set up message handlers before connecting
    this.setupSyncHandlers();

    // Connect and get initial state
    const state = await this.syncClient.connect();

    if (state) {
      // Load state from server
      this.slides = state.slides;
      this.currentSlideIndex = state.currentSlideIndex;
      this.audioSegments = state.audioSegments || [];

      if (this.slides.length > 0) {
        // Clear default players and load from server state
        this.playerLayer.find('.player').forEach(p => p.destroy());

        const slide = this.slides[this.currentSlideIndex];

        // Load slide description
        this.descriptionInput.innerText = slide.description || '';

        // Restore state - convert ratios to current canvas pixels
        slide.canvasState.players.forEach(p => {
          const x = ratioToPixel(p.x, this.CANVAS_WIDTH);
          const y = ratioToPixel(p.y, this.CANVAS_HEIGHT);
          this.createPlayer(x, y, p.color, p.label);
        });

        slide.canvasState.arrows.forEach(a => {
          this.hitCounter++;
          const startX = ratioToPixel(a.startX, this.CANVAS_WIDTH);
          const startY = ratioToPixel(a.startY, this.CANVAS_HEIGHT);
          const endX = ratioToPixel(a.endX, this.CANVAS_WIDTH);
          const endY = ratioToPixel(a.endY, this.CANVAS_HEIGHT);
          const ctrl = a.ctrl ? {
            x: ratioToPixel(a.ctrl.x, this.CANVAS_WIDTH),
            y: ratioToPixel(a.ctrl.y, this.CANVAS_HEIGHT)
          } : null;
          const bounce = a.bounce ? {
            x: ratioToPixel(a.bounce.x, this.CANVAS_WIDTH),
            y: ratioToPixel(a.bounce.y, this.CANVAS_HEIGHT)
          } : null;
          this.createArrow(startX, startY, endX, endY, a.color, a.style, ctrl, bounce, this.hitCounter);
        });
      }

      this.updateSlideIndicator();
      this.updatePaginationButtons();
      this.renderSegmentTimeline();
    } else {
      // Connection failed, fall back to localStorage
      console.warn('WebSocket connection failed, using localStorage');
      if (!this.loadFromLocalStorage()) {
        const firstSlide: Slide = {
          id: this.generateSlideId(),
          description: '',
          canvasState: this.getCurrentCanvasState()
        };
        this.slides = [firstSlide];
        this.currentSlideIndex = 0;
        this.descriptionInput.innerText = '';
        this.updateSlideIndicator();
        this.updatePaginationButtons();
      }
    }
  }

  private setupSyncHandlers(): void {
    if (!this.syncClient) return;

    // Handle slide updates from other clients
    this.syncClient.on('slide-update', (msg: unknown) => {
      const message = msg as { slideIndex: number; canvasState: State };
      if (message.slideIndex === this.currentSlideIndex) {
        // Update current slide - reload canvas
        this.slides[message.slideIndex].canvasState = message.canvasState;
        this.reloadCurrentSlide();
      } else {
        // Update other slide in memory
        this.slides[message.slideIndex].canvasState = message.canvasState;
      }
    });

    // Handle slide additions from other clients
    this.syncClient.on('slide-add', (msg: unknown) => {
      const message = msg as { slide: Slide; insertAfterIndex: number };
      this.slides.splice(message.insertAfterIndex + 1, 0, message.slide);
      this.updateSlideIndicator();
      this.updatePaginationButtons();
    });

    // Handle slide deletions from other clients
    this.syncClient.on('slide-delete', (msg: unknown) => {
      const message = msg as { slideIndex: number };
      if (this.slides.length > 1) {
        this.slides.splice(message.slideIndex, 1);
        if (this.currentSlideIndex >= this.slides.length) {
          this.currentSlideIndex = this.slides.length - 1;
          this.reloadCurrentSlide();
        } else if (this.currentSlideIndex === message.slideIndex) {
          this.reloadCurrentSlide();
        }
        this.updateSlideIndicator();
        this.updatePaginationButtons();
      }
    });

    // Handle description updates from other clients
    this.syncClient.on('description-update', (msg: unknown) => {
      const message = msg as { description: string };
      this.descriptionInput.value = message.description;
    });

    // Handle full sync from other clients
    this.syncClient.on('full-sync', (msg: unknown) => {
      const message = msg as { state: DiagramState };
      this.descriptionInput.value = message.state.description;
      this.slides = message.state.slides;
      this.currentSlideIndex = message.state.currentSlideIndex;
      this.audioSegments = message.state.audioSegments || [];
      this.reloadCurrentSlide();
      this.updateSlideIndicator();
      this.updatePaginationButtons();
      this.renderSegmentTimeline();
    });

    // Handle audio segment additions from other clients
    this.syncClient.on('audio-segment-add', (msg: unknown) => {
      const message = msg as { segment: AudioSegment };
      this.audioSegments.push(message.segment);
      this.renderSegmentTimeline();
    });

    // Handle audio segment deletions from other clients
    this.syncClient.on('audio-segment-delete', (msg: unknown) => {
      const message = msg as { segmentId: string };
      this.audioSegments = this.audioSegments.filter(s => s.id !== message.segmentId);
      this.renderSegmentTimeline();
    });
  }

  private reloadCurrentSlide(): void {
    // Clear canvas
    this.playerLayer.find('.player').forEach(p => p.destroy());
    this.arrowLayer.find('.arrow').forEach(a => a.destroy());
    this.selectedShape = null;
    this.hitCounter = 0;

    // Reload current slide - convert ratios to current canvas pixels
    const slide = this.slides[this.currentSlideIndex];
    if (slide) {
      // Load description
      this.descriptionInput.innerText = slide.description || '';

      slide.canvasState.players.forEach(p => {
        const x = ratioToPixel(p.x, this.CANVAS_WIDTH);
        const y = ratioToPixel(p.y, this.CANVAS_HEIGHT);
        this.createPlayer(x, y, p.color, p.label);
      });

      slide.canvasState.arrows.forEach(a => {
        this.hitCounter++;
        const startX = ratioToPixel(a.startX, this.CANVAS_WIDTH);
        const startY = ratioToPixel(a.startY, this.CANVAS_HEIGHT);
        const endX = ratioToPixel(a.endX, this.CANVAS_WIDTH);
        const endY = ratioToPixel(a.endY, this.CANVAS_HEIGHT);
        const ctrl = a.ctrl ? {
          x: ratioToPixel(a.ctrl.x, this.CANVAS_WIDTH),
          y: ratioToPixel(a.ctrl.y, this.CANVAS_HEIGHT)
        } : null;
        const bounce = a.bounce ? {
          x: ratioToPixel(a.bounce.x, this.CANVAS_WIDTH),
          y: ratioToPixel(a.bounce.y, this.CANVAS_HEIGHT)
        } : null;
        this.createArrow(startX, startY, endX, endY, a.color, a.style, ctrl, bounce, this.hitCounter);
      });
    }

    this.playerLayer.draw();
    this.arrowLayer.draw();
  }

  private syncToServer(): void {
    if (!this.syncClient) return;

    // Debounce sync to avoid flooding server
    clearTimeout(this.syncDebounceTimer);
    this.syncDebounceTimer = window.setTimeout(() => {
      this.saveCurrentSlideToMemory();
      this.syncClient?.sendSlideUpdate(this.currentSlideIndex, this.slides[this.currentSlideIndex].canvasState);
    }, 300);
  }

  destroy(): void {
    document.removeEventListener('keydown', this.boundHandleKeydown);
    window.removeEventListener('resize', this.boundHandleResize);
    clearTimeout(this.resizeTimeout);
    clearTimeout(this.syncDebounceTimer);
    this.syncClient?.disconnect();
    this.audioRecorder?.cancelRecording();
    this.segmentPlayer?.destroy();
    this.stage.destroy();
    this.element.remove();
  }
}
